import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const PHRASE = "DELETE SELECTED CLAIMS";

function RmaList({ items, testId }) {
  return (
    <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 font-mono text-xs text-slate-700" data-testid={testId}>
      {items.map((i) => <div key={i.id || i.rma_number} className="py-0.5">{i.rma_number}{i.reason && <span className="ml-2 font-sans text-rose-600">— {i.reason}</span>}</div>)}
    </div>
  );
}

function BlockedStep({ check, onCancel, onPartial }) {
  return (
    <>
      <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-600"><ShieldAlert className="h-5 w-5" /> Some claims are protected</DialogTitle></DialogHeader>
      <p className="text-sm text-slate-600"><b>Nothing has been deleted.</b> {check.blocked.length} selected claim{check.blocked.length !== 1 ? "s" : ""} cannot be deleted:</p>
      <RmaList items={check.blocked} testId="bulk-blocked-list" />
      {check.allowed.length > 0 && <p className="text-sm text-slate-600">You may delete only the other <b>{check.allowed.length}</b> claim{check.allowed.length !== 1 ? "s" : ""} instead. Protected claims will be kept.</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} data-testid="bulk-blocked-cancel-btn">Cancel</Button>
        {check.allowed.length > 0 && <Button className="bg-rose-600 text-white hover:bg-rose-700" onClick={onPartial} data-testid="bulk-delete-allowed-only-btn">Delete only {check.allowed.length} allowed</Button>}
      </DialogFooter>
    </>
  );
}

export const BulkDeleteDialog = ({ open, onOpenChange, selected, onDone }) => {
  const [step, setStep] = useState(1);
  const [check, setCheck] = useState(null);
  const [partial, setPartial] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState("");

  useEffect(() => {
    if (open) { setStep(1); setCheck(null); setPartial(false); setText(""); setRequestId(crypto.randomUUID()); }
  }, [open]);

  const ids = selected.map((s) => s.id);
  const targets = partial && check ? check.allowed : selected;

  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/claims/bulk-delete/validate", { claim_ids: ids });
      setCheck(data);
      setStep(data.blocked.length ? "blocked" : 2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/claims/bulk-delete", { claim_ids: ids, confirm_text: text.trim(), request_id: requestId, allow_partial: partial });
      toast.success(`Deleted ${data.deleted.length} claim${data.deleted.length !== 1 ? "s" : ""}${data.skipped.length ? ` · ${data.skipped.length} protected claim(s) kept` : ""}`);
      onOpenChange(false);
      onDone(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.blocked) { setCheck({ blocked: d.blocked, allowed: check?.allowed || [] }); setStep("blocked"); }
      toast.error(formatApiError(d));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md" data-testid="bulk-delete-dialog">
        {step === 1 && (
          <>
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-rose-600"><AlertTriangle className="h-5 w-5" /> Delete {selected.length} Warranty Claim{selected.length !== 1 ? "s" : ""}?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600" data-testid="bulk-step1-count">You are about to permanently delete <b>{selected.length}</b> selected claim{selected.length !== 1 ? "s" : ""} and their related warranty data (files, notes, messages, testing, shipments, vendor claims, timeline). <b className="text-rose-600">This cannot be undone.</b></p>
            <RmaList items={selected} testId="bulk-selected-list" />
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-cancel-btn">Cancel</Button>
              <Button className="bg-rose-600 text-white hover:bg-rose-700" onClick={validate} disabled={busy} data-testid="bulk-continue-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}</Button>
            </DialogFooter>
          </>
        )}
        {step === "blocked" && check && <BlockedStep check={check} onCancel={() => onOpenChange(false)} onPartial={() => { setPartial(true); setText(""); setStep(2); }} />}
        {step === 2 && (
          <>
            <DialogHeader><DialogTitle className="text-rose-600">Final Confirmation</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600" data-testid="bulk-step2-count"><b>{targets.length}</b> claim{targets.length !== 1 ? "s" : ""} will be permanently deleted{partial ? `; ${check.blocked.length} protected claim(s) will be kept` : ""}.</p>
            <div>
              <Label className="mb-1.5 block text-sm">Type <span className="font-mono font-semibold text-slate-900">{PHRASE}</span> to confirm</Label>
              <Input data-testid="bulk-confirm-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={PHRASE} autoComplete="off" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button className="bg-rose-600 text-white hover:bg-rose-700" disabled={text.trim() !== PHRASE || busy} onClick={doDelete} data-testid="bulk-delete-final-btn">
                {busy ? "Deleting..." : `DELETE ${targets.length} CLAIM${targets.length !== 1 ? "S" : ""}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
